// D4: Conversation Playbook — win/loss patterns, templates, A/B results
import { useApi } from "../../../hooks/use-api";
import { SkeletonCard } from "../../../components/skeleton";

interface PlaybookPattern {
  pattern: string;
  sampleSize: number;
  matchedDeals: number;
}

interface WinPattern extends PlaybookPattern {
  winRate: number;
}

interface LossPattern extends PlaybookPattern {
  lossRate: number;
}

interface PlaybookTemplate {
  id: string;
  name: string;
  usageCount: number;
  winRate: number;
}

interface PlaybookResponse {
  winPatterns: WinPattern[];
  lossPatterns: LossPattern[];
  templates: PlaybookTemplate[];
  insight?: string;
}

export default function PlaybookPage() {
  const { data, isLoading, error } = useApi<PlaybookResponse>("/crm/intelligence/playbook");
  const isEmpty = !isLoading
    && !error
    && (data?.winPatterns?.length ?? 0) === 0
    && (data?.lossPatterns?.length ?? 0) === 0
    && (data?.templates?.length ?? 0) === 0;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-[var(--color-foreground)] mb-6">Conversation Playbook</h1>
      {isLoading ? <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}</div> : error ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          Failed to load the conversation playbook. {error}
        </div>
      ) : isEmpty ? (
        <div className="rounded-xl border border-dashed p-6 text-sm text-[var(--color-muted-foreground)]">
          <p className="font-medium text-[var(--color-foreground)]">Not enough data yet</p>
          <p className="mt-2">
            Close a few deals and send proposals to unlock winning patterns, loss signals, and template performance.
          </p>
          {data?.insight ? <p className="mt-3">{data.insight}</p> : null}
        </div>
      ) : (
        <>
          {data?.insight ? (
            <div className="mb-6 rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4 text-sm text-[var(--color-muted-foreground)]">
              {data.insight}
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="border rounded-xl p-6 bg-[var(--color-card)]">
              <h2 className="text-lg font-semibold text-[var(--color-success)] mb-4">✅ Winning Patterns</h2>
              {data?.winPatterns?.map((p, i) => (
                <div key={i} className="flex justify-between py-3 border-b last:border-0">
                  <div className="pr-4">
                    <p className="text-sm">{p.pattern}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{p.sampleSize} matched deals</p>
                  </div>
                  <span className="text-sm font-semibold text-[var(--color-success)] whitespace-nowrap">{p.winRate}% win rate</span>
                </div>
              ))}
            </div>
            <div className="border rounded-xl p-6 bg-[var(--color-card)]">
              <h2 className="text-lg font-semibold text-[var(--color-destructive)] mb-4">❌ Loss Patterns</h2>
              {data?.lossPatterns?.map((p, i) => (
                <div key={i} className="flex justify-between py-3 border-b last:border-0">
                  <div className="pr-4">
                    <p className="text-sm">{p.pattern}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{p.sampleSize} matched deals</p>
                  </div>
                  <span className="text-sm font-semibold text-[var(--color-destructive)] whitespace-nowrap">{p.lossRate}% loss rate</span>
                </div>
              ))}
            </div>
          </div>
          <h2 className="text-lg font-semibold mb-3">Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data?.templates?.map((t) => (
              <div key={t.id} className="border rounded-lg p-4 bg-[var(--color-card)] hover:shadow-sm transition-shadow">
                <h3 className="font-medium mb-2">{t.name}</h3>
                <div className="flex justify-between text-sm text-[var(--color-muted-foreground)]">
                  <span>Used {t.usageCount}x</span>
                  <span className="text-[var(--color-primary)]">{t.winRate}% win rate</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
